
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, Filter, TrendingUp, MapPin, Wrench, Building } from "lucide-react";
import { LabourRate } from "../../../types";

const LaborDatabase = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedRegion, setSelectedRegion] = useState<string>("all");

  const [labourRates] = useState<LabourRate[]>([
    {
      id: "LR-001",
      skill: "Mason (Skilled)",
      category: "Construction",
      hourlyRate: 1200,
      dailyRate: 9600,
      region: "Lagos",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-002",
      skill: "Carpenter (Skilled)",
      category: "Construction",
      hourlyRate: 1500,
      dailyRate: 12000,
      region: "Lagos",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-003",
      skill: "Electrician (Skilled)",
      category: "Technical",
      hourlyRate: 1800,
      dailyRate: 14400,
      region: "Lagos",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-004",
      skill: "Labourer (Unskilled)",
      category: "General",
      hourlyRate: 600,
      dailyRate: 4800,
      region: "Lagos",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-005",
      skill: "Mason (Skilled)",
      category: "Construction",
      hourlyRate: 800,
      dailyRate: 6400,
      region: "Kano",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-006",
      skill: "Plumber (Skilled)",
      category: "Technical",
      hourlyRate: 1400,
      dailyRate: 11200,
      region: "Abuja",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-007",
      skill: "Welder (Skilled)",
      category: "Technical",
      hourlyRate: 1600,
      dailyRate: 12800,
      region: "Port Harcourt",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    },
    {
      id: "LR-008",
      skill: "Crane Operator",
      category: "Equipment",
      hourlyRate: 2200,
      dailyRate: 17600,
      region: "Lagos",
      currency: "NGN",
      effectiveDate: "2024-01-01",
      lastUpdated: "2024-01-15"
    }
  ]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'Construction': return Building;
      case 'Technical': return Wrench;
      case 'Equipment': return Users;
      default: return Users;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Construction': return 'bg-blue-500';
      case 'Technical': return 'bg-green-500';
      case 'Equipment': return 'bg-purple-500';
      case 'General': return 'bg-gray-500';
      default: return 'bg-gray-500';
    }
  };

  const filteredRates = labourRates.filter(rate => {
    const matchesSearch = rate.skill.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "all" || rate.category === selectedCategory;
    const matchesRegion = selectedRegion === "all" || rate.region === selectedRegion;
    return matchesSearch && matchesCategory && matchesRegion;
  });

  const categories = Array.from(new Set(labourRates.map(rate => rate.category)));
  const regions = Array.from(new Set(labourRates.map(rate => rate.region)));

  // Calculate summary statistics
  const avgHourlyRate = filteredRates.reduce((sum, rate) => sum + rate.hourlyRate, 0) / filteredRates.length;
  const maxDailyRate = Math.max(...filteredRates.map(rate => rate.dailyRate));
  const minDailyRate = Math.min(...filteredRates.map(rate => rate.dailyRate));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Labour Rate Database</h3>
          <p className="text-gray-600">Regional labour rates by skill category and experience level</p>
        </div>
        <Button>
          <Users className="h-4 w-4 mr-2" />
          Add Labour Rate
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Skills</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredRates.length}</div>
            <p className="text-xs text-muted-foreground">Active rates</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Hourly Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(avgHourlyRate)}</div>
            <p className="text-xs text-muted-foreground">Across all skills</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Highest Daily Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(maxDailyRate)}</div>
            <p className="text-xs text-muted-foreground">Premium skills</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Regions</CardTitle>
            <MapPin className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{regions.length}</div>
            <p className="text-xs text-muted-foreground">Coverage areas</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" />
                <Input
                  placeholder="Search skills..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger className="w-48">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map(category => (
                  <SelectItem key={category} value={category}>{category}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedRegion} onValueChange={setSelectedRegion}>
              <SelectTrigger className="w-48">
                <MapPin className="h-4 w-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Regions</SelectItem>
                {regions.map(region => (
                  <SelectItem key={region} value={region}>{region}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Labour Rates Table */}
      <Card>
        <CardHeader>
          <CardTitle>Labour Rate Schedule</CardTitle>
          <CardDescription>
            Current rates by skill type, category, and region
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Skill Type</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Hourly Rate</TableHead>
                <TableHead>Daily Rate</TableHead>
                <TableHead>Region</TableHead>
                <TableHead>Effective Date</TableHead>
                <TableHead>Variance</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRates.map((rate) => {
                const CategoryIcon = getCategoryIcon(rate.category);
                const variance = ((rate.dailyRate - minDailyRate) / minDailyRate) * 100;
                
                return (
                  <TableRow key={rate.id}>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <CategoryIcon className="h-4 w-4 text-gray-500" />
                        <div>
                          <div className="font-medium">{rate.skill}</div>
                          <div className="text-sm text-gray-600">{rate.id}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${getCategoryColor(rate.category)} text-white`}>
                        {rate.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatCurrency(rate.hourlyRate)}
                      </div>
                      <div className="text-sm text-gray-600">per hour</div>
                    </TableCell>
                    <TableCell>
                      <div className="font-medium">
                        {formatCurrency(rate.dailyRate)}
                      </div>
                      <div className="text-sm text-gray-600">per day (8hrs)</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-1">
                        <MapPin className="h-3 w-3 text-gray-500" />
                        <span>{rate.region}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {new Date(rate.effectiveDate).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <div className={`flex items-center space-x-1 ${
                        variance > 50 ? 'text-red-600' : variance > 20 ? 'text-yellow-600' : 'text-green-600'
                      }`}>
                        <TrendingUp className="h-3 w-3" />
                        <span className="text-sm">{variance.toFixed(0)}%</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        Update Rate
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Regional Comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Regional Rate Comparison</CardTitle>
          <CardDescription>
            Compare labour costs across different regions for key skills
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-4">Skilled Mason Rates</h4>
              <div className="space-y-3">
                {labourRates.filter(rate => rate.skill.includes("Mason")).map((rate) => (
                  <div key={rate.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">{rate.region}</span>
                      <div className="text-sm text-gray-600">Daily rate</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(rate.dailyRate)}</div>
                      <div className="text-sm text-gray-600">
                        {formatCurrency(rate.hourlyRate)}/hr
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-4">Technical Skills Premium</h4>
              <div className="space-y-3">
                {labourRates.filter(rate => rate.category === "Technical").slice(0, 4).map((rate) => (
                  <div key={rate.id} className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                    <div>
                      <span className="font-medium">{rate.skill}</span>
                      <div className="text-sm text-blue-600">{rate.region}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-blue-700">{formatCurrency(rate.dailyRate)}</div>
                      <div className="text-sm text-blue-600">
                        {((rate.dailyRate / minDailyRate - 1) * 100).toFixed(0)}% premium
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default LaborDatabase;

